import React, { useRef, useEffect } from 'react';
import { faCaretDown, faCaretUp, faTh } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styled from 'styled-components';
import tw from 'twin.macro';
import { ProductType } from '../../../__generated__/graphql';
import { PRODUCT_TYPE_OPTIONS, getProductTypeLabel } from '../../constants/productTypeOptions';
import { getSiteConfig } from '../../constants/siteConfig';
import { Colors } from '../../styles/colors';

interface ProductTypePickerProps {
  productType: ProductType;
  onProductTypeChange: (type: ProductType) => void;
  isOpen: boolean;
  onToggle: () => void;
}

const ProductTypePickerContainer = styled.div`
  ${tw`relative`}
`;

const ProductTypeInput = styled.div`
  ${tw`
    h-[44px]
    pl-3
    flex
    items-center
    cursor-pointer
    bg-white
  `}
  border: 1px solid ${Colors.BorderLight};
  border-radius: 8px;

  &:hover {
    border-color: ${Colors.Gold};
  }
`;

const ProductTypeDropdown = styled.div`
  ${tw`
    absolute
    top-12
    left-0
    bg-white
    w-full
    z-[9999]
    max-h-60
    overflow-y-auto
  `}
  border: 1px solid ${Colors.BorderLight};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
`;

const ProductTypeOption = styled.div`
  ${tw`
    p-3
    cursor-pointer
    flex
    justify-between
    items-center
  `}

  &:hover {
    background: ${Colors.GoldLight};
  }
`;

const LocationLabel = styled.div<{ $horizontal?: boolean }>`
  ${tw`
    text-sm
    font-medium
  `}
  ${props => (props.$horizontal ? tw`text-black mb-1` : tw`text-gray-100 mb-1`)}
`;

const Icon = styled.span`
  ${tw`
    fill-current
    text-xs
    md:text-base
    mr-1
    md:mr-3
  `};
  color: ${Colors.Gold};
`;

const SmallIcon = styled.span`
  ${tw`
    text-gray-100
    fill-current
    text-xs
    md:text-base
    ml-1
    pr-2
  `};
`;

const Name = styled.span<{ $horizontal?: boolean }>`
  ${tw`
    text-xs
    md:text-sm
    cursor-pointer
    select-none
  `};
  ${props => (props.$horizontal ? tw`text-text-primary` : tw`text-text-primary`)}
  ${props =>
    props.$horizontal &&
    `
    font-size: 0.7em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  `}
`;

export const ProductTypePicker: React.FC<ProductTypePickerProps> = ({
  productType,
  onProductTypeChange,
  isOpen,
  onToggle,
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        if (isOpen) {
          onToggle();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle]);

  const siteConfig = getSiteConfig();
  const filteredOptions = siteConfig.allowedProductTypes
    ? PRODUCT_TYPE_OPTIONS.filter(
        option =>
          option.value === ProductType.All ||
          siteConfig.allowedProductTypes!.includes(option.value),
      )
    : PRODUCT_TYPE_OPTIONS;

  const handleOptionClick = (type: ProductType) => {
    onProductTypeChange(type);
    if (isOpen) {
      onToggle();
    }
  };

  return (
    <ProductTypePickerContainer ref={pickerRef}>
      <LocationLabel>Look for</LocationLabel>
      <ProductTypeInput onClick={onToggle}>
        <div>
          <Icon>
            <FontAwesomeIcon icon={faTh} />
          </Icon>
          <Name>{getProductTypeLabel(productType)}</Name>
        </div>
        <SmallIcon>
          <FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} />
        </SmallIcon>
      </ProductTypeInput>
      {isOpen && (
        <ProductTypeDropdown>
          {filteredOptions.map(option => (
            <ProductTypeOption key={option.value} onClick={() => handleOptionClick(option.value)}>
              <Name>{option.label}</Name>
              {productType === option.value && <Icon style={{ color: Colors.Gold }}>✓</Icon>}
            </ProductTypeOption>
          ))}
        </ProductTypeDropdown>
      )}
    </ProductTypePickerContainer>
  );
};
